import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core'
import { Message, MessageType } from '@tv3/store/message/message.model'
import * as R from 'ramda'
import { BehaviorSubject, combineLatest, EMPTY, interval, Observable } from 'rxjs'
import {
  AddMessageComplete,
  DeleteMessage,
  GetBookingMessages,
  GetGuestMessages,
  MarkAsRead,
} from '@tv3/store/message/message.actions'
import { Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import * as jQuery from 'jquery'
import { Attachment, unpackSafeHtml, isSomething } from '@tokeet-frontend/tv3-platform'
import { DomSanitizer } from '@angular/platform-browser'
import { Guest } from '@tv3/store/guest/guest.model'
import { filter, map, startWith } from 'rxjs/operators'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { InquirySharedDialogsService } from '@tv3/containers/inquiries/dialogs/inquiry-shared-dialogs.service'
import { DataCheckerService } from '@tokeet-frontend/tv3-platform'
import { MessageListItemComponent } from '@tv3/containers/messages/message-list/message-list-item/message-list-item.component'
import { AddInquiryDialogParamDefaults } from '@tv3/interfaces/dialogs/add-inquiry-dialog-params'
import * as lodash from 'lodash'
import { CdkConnectedOverlay, GlobalPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay'
import { TemplatePortal } from '@angular/cdk/portal'
import { InquiryMessageFormComponent } from '../inquiry-message-form/inquiry-message-form.component'
import { PreferencesGuard } from '@tv3/guards/preferences.guard'
import { Actions, ofType } from '@ngrx/effects'
import { DraftTaskGuard, selectDraftTasksByInquiryId, TaskGuard, TaskResponse } from '@tokeet-frontend/tasks'
import { IncidentResponse, IncidentsGuard, selectIncidentsFromBookingMessages } from '@tokeet-frontend/incident'
import { OpenDraftTasksApprovalDialog } from '@tv3/store/overlay/overlay.actions'
import { ApproveDraftTasksDialogTabs } from '@tv3/containers/tasks/approve-draft-tasks-dialog/approve-draft-tasks-dialog.component'
import { ResizeSensor } from 'css-element-queries'
import { AuthService } from '@tv3/services/auth.service'
import { selectAllUsers } from '@tokeet-frontend/tv3-platform'
import { select } from '@ngrx/store'
import {
  MessageDisplayPreference,
  normalizeMessageDisplayPreference,
  shouldCollapseMessage,
} from '@tv3/interfaces/inbox-message-display-preference'

declare const document: any

@Component({
  selector: 'app-message-list',
  templateUrl: './message-list.component.html',
  styleUrls: ['./message-list.component.scss'],
})
export class MessageListComponent extends Destroyable implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('messageFormTmpl') messageFormTmpl: TemplateRef<unknown>
  @ViewChild('editor') messageEditor: InquiryMessageFormComponent
  @ViewChild('inlineOverlayRef') inlineMsgFormOverlayRef: CdkConnectedOverlay

  messageFormOverlayRef: OverlayRef = this.overlay.create({ hasBackdrop: true, width: '80%' })

  _messages: Message[] = []
  @Input() set messages(msgs: Message[]) {
    this._messages = this.processMessages(R.filter((m) => m.type !== MessageType.Draft, msgs || []))
    this.draftMessage = R.find((m) => m.type === MessageType.Draft, msgs || [])
  }
  get messages(): Message[] {
    return this._messages || []
  }

  _inquiry: Inquiry | undefined
  @Input() set inquiry(inquiry: Inquiry) {
    this._inquiry = inquiry
    if (inquiry?.id) {
      this.incidentsFromMessages$ = this.store.select(selectIncidentsFromBookingMessages(inquiry?.id))
      this.draftTasksForBooking$ = this.store.select(selectDraftTasksByInquiryId(inquiry?.id))
    } else {
      this.incidentsFromMessages$ = EMPTY
      this.draftTasksForBooking$ = EMPTY
    }
  }
  get inquiry(): Inquiry | undefined {
    return this._inquiry
  }

  @Input() guest?: Guest
  @Input() messageId?: string
  @Input() hasHeader = false
  @Input() hasMove = true
  @Input() autoOpenMessageEditor = false
  @Input() isInbox = false
  @Input() set emptyTableType(type: TableType) {
    if (type) {
      this.tableType = type
    }
  }

  @ViewChildren('msg') messageComponents: QueryList<MessageListItemComponent>

  searchTerm$ = new BehaviorSubject('')

  filteredMessages: Message[]
  trackByMessageId = (index: number, message: Message) => message.id

  isInlineMessageFormVisible = false
  isMessageFormPopup = false

  draftMessage: Message

  tableType = TableType.GuestEmailsList
  incidentsFromMessages$: Observable<IncidentResponse[]>
  draftTasksForBooking$: Observable<TaskResponse[]>

  private resizeObserver: ResizeSensor
  private scrollListener: () => void

  messagingBoxLocation: 'closed' | 'top' | 'bottom' = 'closed'
  messageDisplay = MessageDisplayPreference.ExpandLatest

  constructor(
    private _viewContainerRef: ViewContainerRef,
    private _elementRef: ElementRef,
    private store: Store<fromRoot.State>,
    private actions: Actions,
    private dataChecker: DataCheckerService,
    private inquirySharedDialogsService: InquirySharedDialogsService,
    private sanitized: DomSanitizer,
    private overlay: Overlay,
    private auth: AuthService
  ) {
    super()

    this.dataChecker.check([PreferencesGuard, DraftTaskGuard, TaskGuard, IncidentsGuard])
    this.resizeObserver = new ResizeSensor(this._elementRef.nativeElement, () => {
      this.updateInlineMessageFormPosition()
    })

    // Get user's messaging box location preference
    this.messagingBoxLocation = this.auth.user?.attributes?.messaging_box_location || 'closed'
    this.messageDisplay = normalizeMessageDisplayPreference(this.auth.user?.attributes?.inbox_message_display)
    this.isInlineMessageFormVisible = this.messagingBoxLocation === 'top' || this.messagingBoxLocation === 'bottom'
  }

  ngOnInit() {
    this.markAllAsRead(this.messages)
    // Subscribe to user changes to keep messaging box location up to date
    this.store.pipe(select(selectAllUsers), untilDestroy(this)).subscribe((users) => {
      const currentUser = R.find((u) => u.id === this.auth.user?.id, users)
      if (currentUser) {
        const newLocation = currentUser.attributes?.messaging_box_location || 'closed'
        const previousLocation = this.messagingBoxLocation
        this.messagingBoxLocation = newLocation
        this.messageDisplay = normalizeMessageDisplayPreference(currentUser.attributes?.inbox_message_display)

        // If location changed, handle opening/closing the form
        if (previousLocation !== newLocation) {
          this.handleMessagingBoxLocationChange(previousLocation, newLocation)
        }
      }
    })

    interval(10 * 1000)
      .pipe(
        // don't refresh the open conversation while the tab is hidden
        filter(() => document.visibilityState === 'visible'),
        untilDestroy(this)
      )
      .subscribe(() => {
        if (this.inquiry?.id) {
          this.store.dispatch(GetBookingMessages({ bookingId: this.inquiry?.id, background: true }))
        } else if (this.guest?.id) {
          this.store.dispatch(GetGuestMessages({ guestId: this.guest?.id, background: true }))
        }
      })

    this.actions.pipe(ofType(AddMessageComplete), untilDestroy(this)).subscribe((action) => {
      if (this.draftMessage) {
        this.store.dispatch(DeleteMessage({ message: this.draftMessage, inquiryId: this.inquiry.id, silent: true }))
      }
    })

    this.searchTerm$
      .pipe(
        filter((term) => R.is(String, term)),
        map((term) => term.toLowerCase()),
        untilDestroy(this)
      )
      .subscribe((term) => {
        if (R.isEmpty(term)) {
          this.filteredMessages = this.messages
        } else {
          this.filteredMessages = R.filter((m: Message) => {
            let html = (unpackSafeHtml(m.messageHtml) + '').toLowerCase()
            const guestName = (m.guestName + '').toLowerCase()
            return html.indexOf(term) !== -1 || guestName.indexOf(term) !== -1
          }, <any>this.messages)
        }
      })

    if (this.autoOpenMessageEditor || this.messagingBoxLocation === 'top' || this.messagingBoxLocation === 'bottom') {
      setTimeout(() => this.openMessageForm(), 100)
    }

    // Add scroll listener to update overlay position
    this.setupScrollListener()
  }

  ngOnChanges() {
    this.searchTerm$.next(this.searchTerm$.value)
  }

  ngAfterViewInit() {
    setTimeout(() => {
      let element = document.getElementById(this.messageId)
      if (element) {
        element.scrollIntoView()
      }
    }, 250)
  }

  ngOnDestroy(): void {
    super.ngOnDestroy()
    if (this.resizeObserver) {
      this.resizeObserver.detach()
    }
    // Clean up scroll listener
    this.removeScrollListener()
  }

  onOpenInquiry() {
    let defaults = {} as AddInquiryDialogParamDefaults
    const guestId: string = R.path(['id'], this.guest)
    if (guestId) {
      defaults = {
        guestId,
      }
    }
    this.inquirySharedDialogsService.openAddInquiry({ defaults })
  }

  markAllAsRead(messages: Message[]) {
    const unreadMessageIds = R.pipe(
      R.filter((m: Message) => !m.read),
      R.map((m: Message) => m.id)
    )(messages)

    if (unreadMessageIds && unreadMessageIds.length > 0) {
      this.store.dispatch(MarkAsRead({ messageIds: unreadMessageIds }))
    }
  }

  toggleCollapsing(collapsed: boolean) {
    this.messageComponents.forEach((m) => m.toggleCollapsing(collapsed))
  }

  get areAllMessagesCollapsed(): boolean {
    if (!this.messages.length) {
      return false
    }

    if (this.messageComponents?.length === this.messages.length) {
      return this.messageComponents.toArray().every((message) => message.collapsed)
    }

    return this.messages.every((message, index) => this.isMessageCollapsed(index))
  }

  isMessageCollapsed(index: number): boolean {
    return shouldCollapseMessage(this.messageDisplay, index)
  }

  private processMessages(messages: Message[]): Message[] {
    const process = R.map((m: Message) => {
      if (m.isProcessed) {
        return m
      }

      const processedMessage = new Message(m)
      processedMessage.isSending = lodash.split(processedMessage.id, '-').length < 5

      let messageTxt = lodash.toString(processedMessage.messageTxt).trim()
      messageTxt = messageTxt.replace(/\n/gi, '<br/>')

      let messageHtml = lodash.toString(processedMessage.messageHtml || '') || messageTxt
      messageHtml = `<div>${messageHtml.replace(/<!--(.*?)-->/gi, '')}</div>` // remove comments

      const $messageHtml = jQuery('style,link,script', messageHtml).remove().end() // remove style, link and script tag

      processedMessage.innerAttachments = jQuery('.attachments', messageHtml).length > 0
      processedMessage.sanitizedMsgHtml = <string>(
        this.sanitized.bypassSecurityTrustHtml(`<div>${$messageHtml.html()}</div>`)
      )

      // Format the Text message
      processedMessage.messageTxt = messageTxt

      try {
        processedMessage.attachments = <Attachment[]>JSON.parse(<any>processedMessage.attachments || null) || []
      } catch (e) {}

      processedMessage.isProcessed = true

      return processedMessage
    })

    const filteredMessages = R.filter((m: Message) => isSomething(m.messageTxt) || isSomething(m.attachments))

    const sort = R.sortBy((m: Message) => -m.receivedOn)

    return <any>R.pipe(process, sort)(<any>messages || [])
  }

  getViewRect() {
    return this._elementRef.nativeElement.getBoundingClientRect()
  }

  onPopup() {
    this.isInlineMessageFormVisible = false
    this.isMessageFormPopup = true
    const portal = new TemplatePortal(this.messageFormTmpl, this._viewContainerRef)
    const elm: HTMLElement = this._viewContainerRef.element.nativeElement
    // const rect = elm.getBoundingClientRect()

    const position = new GlobalPositionStrategy()
    position.centerHorizontally().centerVertically()
    this.messageFormOverlayRef.updatePositionStrategy(position)
    this.messageFormOverlayRef.attach(portal)

    // TV4-1948: we need to subscribe to "keydownEvents"
    // so "Overlay" doesn't trigger close on parent overlays
    this.messageFormOverlayRef.keydownEvents().subscribe()
  }

  openMessageForm() {
    this.isInlineMessageFormVisible = true
    this.isMessageFormPopup = false

    // Ensure proper positioning when overlay opens
    if (this.messagingBoxLocation === 'closed') {
      setTimeout(() => {
        this.updateInlineMessageFormPosition()
      }, 0)
    }
  }

  closeMessageForm() {
    console.log('closeMessageForm')
    this.isInlineMessageFormVisible = false
    if (this.isMessageFormPopup) {
      this.messageFormOverlayRef.detach()
    }
    this.isMessageFormPopup = false
  }

  updateInlineMessageFormPosition() {
    this.inlineMsgFormOverlayRef?.overlayRef?.updatePosition()
  }

  onEditDraft(msg: Message) {
    this.draftMessage = msg
    this.openMessageForm()
  }

  onAIGenerate() {
    this.openMessageForm()
    setTimeout(() => {
      this.messageEditor?.onAiResponder()
    }, 100)
  }

  onOpenTaskApproval() {
    this.store.dispatch(
      OpenDraftTasksApprovalDialog({ data: { tab: ApproveDraftTasksDialogTabs.Message, inquiryId: this.inquiry?.id } })
    )
  }

  private setupScrollListener() {
    this.scrollListener = () => {
      if (this.isInlineMessageFormVisible && this.inlineMsgFormOverlayRef) {
        // Use requestAnimationFrame for smooth updates
        requestAnimationFrame(() => {
          this.updateInlineMessageFormPosition()
        })
      }
    }

    // Listen to scroll events on window and any scrollable containers
    window.addEventListener('scroll', this.scrollListener, true)
    document.addEventListener('scroll', this.scrollListener, true)

    // Also listen to resize events which might affect positioning
    window.addEventListener('resize', this.scrollListener, true)
  }

  private removeScrollListener() {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true)
      document.removeEventListener('scroll', this.scrollListener, true)
      window.removeEventListener('resize', this.scrollListener, true)
    }
  }

  private handleMessagingBoxLocationChange(
    previousLocation: 'closed' | 'top' | 'bottom',
    newLocation: 'closed' | 'top' | 'bottom'
  ) {
    // If changing from 'closed' to 'top' or 'bottom', open the form
    if (previousLocation === 'closed' && (newLocation === 'top' || newLocation === 'bottom')) {
      setTimeout(() => this.openMessageForm(), 100)
    }
    // If changing from 'top' or 'bottom' to 'closed', close the form
    else if ((previousLocation === 'top' || previousLocation === 'bottom') && newLocation === 'closed') {
      this.closeMessageForm()
    }
    // If changing between 'top' and 'bottom', keep the form open (it's already handled by the template)
  }
}
